/**
 * "Where does it go" — a dropdown, in the bottom-left of a modal.
 *
 * Not a native `<select>`: those render as the OS widget, ignore the app's
 * theme, and cannot show the little diagram that makes four spatial choices
 * readable at a glance. This is a button plus a small menu, themed like the rest
 * of the product.
 *
 * It belongs in the footer's left edge, opposite Cancel and the primary button:
 * it qualifies the action you are about to take, so it sits ON that row, at the
 * far end from the buttons that commit.
 *
 * DUMB ON PURPOSE. It takes labels, not keys, and reports the value it was
 * given — the editor resolves placement against a real source offset and the
 * copy step against the same selection, and neither semantics belongs in the
 * thing that draws a menu.
 */
import { useEffect, useRef, useState } from 'react'

export interface PlacementOption<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  label: string
  options: ReadonlyArray<PlacementOption<T>>
  value: T
  onChange: (value: T) => void
  disabled?: boolean
  /** Which edge the menu lines up with. `right` when the picker sits at the
   *  right of its row, so the wider menu does not hang off the dialog. */
  menuAlign?: 'left' | 'right'
}

/**
 * A 14×14 diagram of the relationship: a dashed box for the element you picked,
 * a solid bar for the icon. It is what lets the closed button say "After it" and
 * still show which "after" it means.
 */
function Diagram({ value }: { value: string }) {
  const box = <rect x="2.5" y="4" width="9" height="6" rx="1.5" fill="none" stroke="currentColor" strokeOpacity="0.5" strokeDasharray="2 1.5" />
  const bar = (y: number) => <rect x="3.5" y={y} width="7" height="1.8" rx="0.9" fill="currentColor" />
  const marks: Record<string, React.ReactNode> = {
    before: <>{bar(0.8)}{box}</>,
    after: <>{box}{bar(11.4)}</>,
    'inside-start': <>{box}{bar(5)}</>,
    'inside-end': <>{box}{bar(7.2)}</>,
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" className="shrink-0">
      {marks[value] ?? box}
    </svg>
  )
}

export function PlacementPicker<T extends string>({
  label, options, value, onChange, disabled = false, menuAlign = 'left',
}: Props<T>) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Outside-click and Escape close it. Escape is captured and stopped so it
  // dismisses THIS menu and not the dialog underneath — the same rule the
  // product's other popovers follow.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  if (options.length === 0) return null
  const current = options.find((o) => o.value === value) ?? options[0]!

  return (
    <div ref={ref} className="relative min-w-0 shrink" data-testid="placement-picker">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current.label}`}
        title={`${label}: ${current.label}`}
        data-testid="placement-trigger"
        className="flex items-center gap-1.5 w-full min-w-0 max-w-[11rem] px-2 py-1.5 rounded-lg border themed-border themed-bg-tertiary themed-text-secondary hover:themed-text transition-colors disabled:opacity-40"
      >
        <Diagram value={current.value} />
        <span className="text-xs truncate min-w-0 flex-1 text-left">{current.label}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="shrink-0 opacity-60">
          <path d="M2 4l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        // Opens UPWARD: it lives on the footer, so a downward menu would fall
        // off the bottom of the dialog.
        <div
          role="listbox"
          aria-label={label}
          className={`absolute bottom-full ${menuAlign === 'right' ? 'right-0' : 'left-0'} mb-1 z-10 min-w-[12rem] rounded-lg border themed-border themed-bg-secondary shadow-xl py-1`}
        >
          {options.map((o) => {
            const on = o.value === value
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { onChange(o.value); setOpen(false) }}
                data-testid={`placement-${o.value}`}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${
                  on ? 'themed-text' : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
                }`}
              >
                <Diagram value={o.value} />
                <span className="text-xs truncate flex-1">{o.label}</span>
                {on && (
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="shrink-0">
                    <path d="M2.5 6.5l2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
